import { ReactNode } from "react";
import { IDetalleCupo } from "../../types";

const KNOWN_KEYS: (keyof IDetalleCupo | "estado")[] = [
  "detalle",
  "estadoSincronización",
  "disponibleObligatorio",
  "disponibleOptativo",
  "hayInfoCuadratura",
  "disponibleObligatorioOracleCuadratura",
  "disponibleOptativoOracleCuadratura",
  "comentarioCuadratura",
  "fechaCuadratura",
  "cumpleCuadratura",
  "id_cupo",
  "añoLectivo",
  "curso",
  "identificacion",
  "programa",
  "orientacion",
  "tipo_cupo",
  "cupo_obligatorio_total",
  "cupo_optativo_total",
  "cupo_obligatorio_confirmado",
  "cupo_optativo_confirmado",
  "cupo_obligatorio_inscriptos",
  "cupo_optativo_inscriptos",
  "cupo_obligatorio_desinscripciones_confirmado",
  "cupo_optativo_desinscripciones_confirmado",
  "fecha_actualizacion_oracle",
  "fecha_actualizacion_cloud",
  "estado",
  "disponible_optativo",
  "disponible_obligatorio",
  "es_liberado",
  "fecha_liberacion",
  "capacidad_aula",
];

const formatFecha = (value: string | null | undefined) => {
  if (!value || value === "-") return "-";
  const parsed = new Date(value.replace(" ", "T"));
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString("es-AR");
};

const formatValor = (value: unknown) => {
  if (value === null) return "sin calcular";
  if (value === undefined || value === "") return "-";
  if (typeof value === "boolean") return value ? "Sí" : "No";
  return String(value);
};

const estadoLiberacion = (esLiberado: boolean | null) => {
  if (esLiberado === true) {
    return {
      short: "Liberado",
      long: "El cupo fue calculado y está liberado. La disponibilidad usa la capacidad de aula.",
      valor: "true",
    };
  }
  if (esLiberado === false) {
    return {
      short: "No liberado",
      long: "El cupo fue calculado y no está liberado. La disponibilidad sigue las columnas de core_cupos.",
      valor: "false",
    };
  }
  return {
    short: "Sin calcular",
    long: "Todavía no se calculó si el cupo está liberado.",
    valor: "null",
  };
};

function Campo({
  label,
  value,
  hint,
  description,
}: {
  label: string;
  value: ReactNode;
  hint?: string;
  description?: string;
}) {
  return (
    <div className="flex-grow min-w-[120px]">
      <p className="border-b-[1px] border-cardBorder text-secondary text-[12px]">
        {label}
      </p>
      {hint && (
        <p className="text-[10px] text-secondary font-mono leading-tight mt-0.5">
          {hint}
        </p>
      )}
      <h5 className="font-semibold">{value}</h5>
      {description && (
        <p className="text-[11px] text-secondary">{description}</p>
      )}
    </div>
  );
}

function CeldaCupo({
  titulo,
  columna,
  valor,
}: {
  titulo: string;
  columna: string;
  valor: unknown;
}) {
  return (
    <td className="p-1 align-top">
      <span className="font-semibold">{formatValor(valor)}</span>
      <p className="text-[10px] text-secondary font-mono leading-tight mt-0.5">
        {columna}
      </p>
    </td>
  );
}

function Seccion({
  titulo,
  children,
}: {
  titulo: string;
  children: ReactNode;
}) {
  return (
    <div>
      <h4 className="font-bold pb-2">{titulo}</h4>
      <div className="flex flex-wrap justify-start items-start gap-6">
        {children}
      </div>
    </div>
  );
}

interface DetalleCupoCardProps {
  detalle: IDetalleCupo;
}

export default function DetalleCupoCard({ detalle }: DetalleCupoCardProps) {
  const extraKeys = Object.keys(detalle).filter(
    (key) => !KNOWN_KEYS.includes(key as keyof IDetalleCupo)
  );
  const detalleRecord = detalle as IDetalleCupo & Record<string, unknown>;
  const liberacion = estadoLiberacion(detalle.es_liberado);

  return (
    <div className="border-[1px] border-secondary p-3 my-4 space-y-4">
      <div className="bg-[#eee] flex flex-wrap justify-between items-center gap-3 p-2">
        <h3 className="font-semibold text-[14px] flex-grow">
          <span className="text-secondary font-normal mr-3">
            #{detalle.id_cupo}
          </span>
          {detalle.detalle}
        </h3>
        <div className="flex items-center gap-2">
          <span className="text-[12px] font-semibold border border-cardBorder bg-white px-2 py-0.5 rounded-md">
            {detalle.estadoSincronización}
          </span>
          {detalle.es_liberado === true && (
            <span className="text-[12px] font-semibold text-white bg-primary px-2 py-0.5 rounded-md">
              Liberado
            </span>
          )}
          {detalle.es_liberado === false && (
            <span className="text-[12px] font-semibold border border-cardBorder bg-white px-2 py-0.5 rounded-md">
              No liberado
            </span>
          )}
          {detalle.es_liberado === null && (
            <span className="text-[12px] font-semibold border border-cardBorder bg-white px-2 py-0.5 rounded-md">
              Liberación sin calcular
            </span>
          )}
        </div>
      </div>

      <Seccion titulo="Identificación">
        <Campo label="Curso" value={formatValor(detalle.curso)} />
        <Campo label="Año lectivo" value={formatValor(detalle.añoLectivo)} />
        <Campo
          label="Identificación"
          value={formatValor(detalle.identificacion)}
        />
        <Campo label="Programa" value={formatValor(detalle.programa)} />
        <Campo label="Orientación" value={formatValor(detalle.orientacion)} />
        <Campo label="Tipo de cupo" value={formatValor(detalle.tipo_cupo)} />
      </Seccion>

      <div>
        <h4 className="font-bold pb-2">Disponibilidad</h4>
        <div className="grid grid-cols-2 gap-6">
          <Campo
            label="Disponible obligatorio"
            hint="disponibleObligatorio"
            value={formatValor(detalle.disponibleObligatorio)}
          />
          <Campo
            label="Disponible optativo"
            hint="disponibleOptativo"
            value={formatValor(detalle.disponibleOptativo)}
          />
          {detalle.es_liberado !== true && (
            <>
              <Campo
                label="disponible_obligatorio"
                hint="columna autocalculada de core_cupos"
                value={formatValor(detalle.disponible_obligatorio)}
              />
              <Campo
                label="disponible_optativo"
                hint="columna autocalculada de core_cupos"
                value={formatValor(detalle.disponible_optativo)}
              />
            </>
          )}
        </div>
      </div>

      <div>
        <h4 className="font-bold pb-2">Cupos</h4>
        <table className="table-auto w-full border-[1px] border-cardBorder border-collapse">
          <thead>
            <tr className="border-[1px] border-cardBorder bg-[#eee]">
              <th className="p-1 text-left"></th>
              <th className="p-1 text-left">Total</th>
              <th className="p-1 text-left">Confirmado</th>
              <th className="p-1 text-left">Inscriptos</th>
              <th className="p-1 text-left">Desinscripciones confirmadas</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-[1px] border-cardBorder">
              <td className="p-1 font-semibold text-secondary">Obligatorio</td>
              <CeldaCupo
                titulo="Total"
                columna="cupo_obligatorio_total"
                valor={detalle.cupo_obligatorio_total}
              />
              <CeldaCupo
                titulo="Confirmado"
                columna="cupo_obligatorio_confirmado"
                valor={detalle.cupo_obligatorio_confirmado}
              />
              <CeldaCupo
                titulo="Inscriptos"
                columna="cupo_obligatorio_inscriptos"
                valor={detalle.cupo_obligatorio_inscriptos}
              />
              <CeldaCupo
                titulo="Desinscripciones confirmadas"
                columna="cupo_obligatorio_desinscripciones_confirmado"
                valor={detalle.cupo_obligatorio_desinscripciones_confirmado}
              />
            </tr>
            <tr className="border-[1px] border-cardBorder">
              <td className="p-1 font-semibold text-secondary">Optativo</td>
              <CeldaCupo
                titulo="Total"
                columna="cupo_optativo_total"
                valor={detalle.cupo_optativo_total}
              />
              <CeldaCupo
                titulo="Confirmado"
                columna="cupo_optativo_confirmado"
                valor={detalle.cupo_optativo_confirmado}
              />
              <CeldaCupo
                titulo="Inscriptos"
                columna="cupo_optativo_inscriptos"
                valor={detalle.cupo_optativo_inscriptos}
              />
              <CeldaCupo
                titulo="Desinscripciones confirmadas"
                columna="cupo_optativo_desinscripciones_confirmado"
                valor={detalle.cupo_optativo_desinscripciones_confirmado}
              />
            </tr>
          </tbody>
        </table>
      </div>

      <div>
        <h4 className="font-bold pb-2">Cuadratura</h4>
        {detalle.hayInfoCuadratura === "No" ? (
          <p className="text-secondary">Sin información de cuadratura</p>
        ) : (
          <div className="flex flex-wrap justify-start items-start gap-6">
            <Campo
              label="Hay información"
              value={formatValor(detalle.hayInfoCuadratura)}
            />
            <Campo
              label="Cumple cuadratura"
              value={formatValor(detalle.cumpleCuadratura)}
            />
            <Campo
              label="Disponible obligatorio Oracle"
              value={formatValor(detalle.disponibleObligatorioOracleCuadratura)}
            />
            <Campo
              label="Disponible optativo Oracle"
              value={formatValor(detalle.disponibleOptativoOracleCuadratura)}
            />
            <Campo
              label="Comentario"
              value={formatValor(detalle.comentarioCuadratura)}
            />
            <Campo
              label="Fecha"
              value={formatFecha(detalle.fechaCuadratura)}
            />
          </div>
        )}
      </div>

      <div>
        <h4 className="font-bold pb-2">Liberación</h4>
        <div className="border-[1px] border-cardBorder rounded-md p-3 space-y-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-secondary text-[12px]">es_liberado</p>
              <h5 className="font-semibold text-[16px]">{liberacion.short}</h5>
              <p className="text-[12px] text-secondary mt-1 max-w-xl">
                {liberacion.long}
              </p>
            </div>
            <span
              className={`text-[12px] font-semibold px-2 py-0.5 rounded-md font-mono ${
                detalle.es_liberado === true
                  ? "text-white bg-primary"
                  : "border border-cardBorder bg-white"
              }`}
            >
              {liberacion.valor}
            </span>
          </div>
          <div className="flex flex-wrap justify-start items-start gap-6">
            <Campo
              label="Fecha de liberación"
              hint="fecha_liberacion"
              value={
                detalle.es_liberado === null
                  ? "sin calcular"
                  : formatFecha(detalle.fecha_liberacion)
              }
            />
            <Campo
              label="Capacidad de aula"
              hint="capacidad_aula"
              value={
                detalle.capacidad_aula == null
                  ? "-"
                  : formatValor(detalle.capacidad_aula)
              }
              description={
                detalle.es_liberado === true
                  ? "Se usa para calcular disponibleObligatorio y disponibleOptativo"
                  : undefined
              }
            />
          </div>
        </div>
      </div>

      <Seccion titulo="Actualizaciones">
        <Campo
          label="Fecha actualización Oracle"
          value={formatFecha(detalle.fecha_actualizacion_oracle)}
        />
        <Campo
          label="Fecha actualización Cloud"
          value={formatFecha(detalle.fecha_actualizacion_cloud)}
        />
      </Seccion>

      {extraKeys.length > 0 && (
        <Seccion titulo="Otros datos">
          {extraKeys.map((key) => (
            <Campo
              key={key}
              label={key}
              value={formatValor(detalleRecord[key])}
            />
          ))}
        </Seccion>
      )}
    </div>
  );
}
